import { PlusIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";

import { Button } from "V2.0/common/button";
import EvalRunConfigExpectedEventTable from "./EvalRunConfigExpectedEventTable";
import EvalRunConfigExpectedEventFormDialog from "./EvalRunConfigExpectedEventFormDialog";
import { EvalEventLabels } from "../../../types";

export default EvalRunConfigExpectedEvent;

function EvalRunConfigExpectedEvent(props) {
  const { personas, eventsToTrack, expectedEvents, onUpdateExpectedEvents } =
    props;

  const [expectedEventFormDialog, setExpectedEventFormDialog] = useState({
    mode: undefined,
    expectedEvent: undefined,
    expectedEventIdx: -1,
  });

  const personaNames = useMemo(() => {
    if (personas.length === 0) return [];

    return personas.map((persona) => persona.name);
  }, [personas]);

  useEffect(() => {
    const updatedExpectedEvents = expectedEvents.filter(
      (expectedEvent) =>
        personaNames.includes(expectedEvent.persona) &&
        eventsToTrack.includes(expectedEvent.event),
    );

    onUpdateExpectedEvents(updatedExpectedEvents);
  }, [personaNames, eventsToTrack]);

  const personaOptions = useMemo(() => {
    const selectedPersonas = expectedEvents.map(
      (expectedEvent) => expectedEvent.persona,
    );
    const filterSelectedPersonas = personaNames.filter(
      (personaName) => !selectedPersonas.includes(personaName),
    );
    return filterSelectedPersonas.reduce(
      (acc, persona, idx) => {
        acc.push({ id: idx + 1, value: persona, label: persona });
        return acc;
      },
      [{ id: 0, value: "", label: "None Selected" }],
    );
  }, [personaNames, expectedEvents]);

  const eventsToTrackOptions = useMemo(() => {
    const selectedEvents = expectedEvents.map(
      (expectedEvent) => expectedEvent.event,
    );
    const filterSelectedEvents = eventsToTrack.filter(
      (event) => !selectedEvents.includes(event),
    );

    return filterSelectedEvents.reduce(
      (acc, event, idx) => {
        acc.push({ id: idx + 1, value: event, label: EvalEventLabels[event] });
        return acc;
      },
      [{ id: 0, value: "", label: "None Selected" }],
    );
  }, [eventsToTrack, expectedEvents]);

  const handleAddExpectedEvent = (expectedEvent) => {
    const updatedExpectedEvents = [...expectedEvents, expectedEvent];
    onUpdateExpectedEvents(updatedExpectedEvents);
  };

  const handleRemoveExpectedEvent = (expectedEventIdx) => {
    const updatedExpectedEvents = expectedEvents.toSpliced(expectedEventIdx, 1);
    onUpdateExpectedEvents(updatedExpectedEvents);
  };

  const handleUpdateExpectedEvent = (expectedEvent, expectedEventIdx) => {
    const updatedExpectedEvents = expectedEvents.toSpliced(
      expectedEventIdx,
      1,
      expectedEvent,
    );
    onUpdateExpectedEvents(updatedExpectedEvents);
  };

  const isExpectedEventDialogModeAdd = expectedEventFormDialog.mode === "add";

  const expectedEventDialogAddModeProps = {
    title: "Add Expected Event",
    confirmCtaLabel: "Add",
    expectedEvent: {},
    onConfirm: handleAddExpectedEvent,
  };

  const expectedEventDialogEditModeProps = {
    title: "Update Expected Event",
    confirmCtaLabel: "Save",
    expectedEvent: expectedEventFormDialog.expectedEvent,
    onConfirm: (expectedEvent) =>
      handleUpdateExpectedEvent(
        expectedEvent,
        expectedEventFormDialog.expectedEventIdx,
      ),
  };

  return (
    <>
      <div className="flex flex-col gap-y-2">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">Expected Events</p>
        </div>

        <div>
          <EvalRunConfigExpectedEventTable
            expectedEvents={expectedEvents}
            onEditExpectedEvent={(expectedEvent, expectedEventIdx) => {
              setExpectedEventFormDialog({
                mode: "edit",
                expectedEvent,
                expectedEventIdx,
              });
            }}
            onDeleteExpectedEvent={handleRemoveExpectedEvent}
          />
        </div>
        <div>
          <Button
            plain
            onClick={() => {
              setExpectedEventFormDialog({
                mode: "add",
                expectedEvent: {},
                expectedEventIdx: -1,
              });
            }}
          >
            <PlusIcon /> Add
          </Button>
        </div>
      </div>

      {expectedEventFormDialog.mode && (
        <EvalRunConfigExpectedEventFormDialog
          {...(isExpectedEventDialogModeAdd
            ? expectedEventDialogAddModeProps
            : expectedEventDialogEditModeProps)}
          personaOptions={personaOptions}
          eventsToTrackOptions={eventsToTrackOptions}
          onClose={() =>
            setExpectedEventFormDialog({
              mode: undefined,
              expectedEvent: undefined,
              expectedEventIdx: -1,
            })
          }
        />
      )}
    </>
  );
}
